import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';

const H2 = ({ children }) => (
  <h2 className="text-lg font-heading font-bold uppercase tracking-wider text-[#00F0FF] mt-10 mb-3">{children}</h2>
);
const P = ({ children }) => <p className="text-sm text-slate-300 leading-relaxed mb-3">{children}</p>;
const LI = ({ children }) => <li className="text-sm text-slate-300 leading-relaxed">{children}</li>;

export default function Privacy() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen ept-bg-ambient" data-testid="privacy-page">
      <nav className="sticky top-0 z-40 bg-[#0a0d15]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            className="text-slate-400 hover:text-white transition-colors flex items-center gap-2 text-sm"
            data-testid="privacy-back-btn"
          >
            <ArrowLeft size={16} /> Indietro
          </button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-10 pb-24">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="text-[#00F0FF]" size={28} />
          <h1 className="text-2xl sm:text-3xl font-heading font-bold uppercase tracking-wider text-white">
            Privacy &amp; Cookie Policy
          </h1>
        </div>
        <p className="text-xs text-slate-500 mb-8">Ultimo aggiornamento: giugno 2026</p>

        <H2>1. Titolare del trattamento</H2>
        <P>
          La presente informativa descrive le modalità di trattamento dei dati personali degli utenti che
          visitano e utilizzano la piattaforma <strong>EPT Italy</strong> (www.ept-italy.it), gestita dallo
          staff EPT Italy. Per qualsiasi richiesta relativa alla privacy è possibile contattarci tramite i
          canali ufficiali indicati sul sito.
        </P>

        <H2>2. Dati raccolti</H2>
        <ul className="list-disc pl-5 space-y-1 mb-3">
          <LI><strong>Dati di registrazione</strong>: nome, email, gamertag, piattaforma di gioco (PS5/Xbox/PC), forniti volontariamente alla creazione dell'account.</LI>
          <LI><strong>Dati di utilizzo</strong>: partite, report, messaggi in chat, statistiche di gioco e attività sulla piattaforma.</LI>
          <LI><strong>Dati tecnici</strong>: indirizzo IP, tipo di browser e dispositivo, raccolti automaticamente per sicurezza e funzionamento del servizio.</LI>
          <LI><strong>Dati pubblici EA Sports</strong>: statistiche dei club Pro Clubs recuperate dalle API pubbliche EA, se colleghi il tuo club o gamertag.</LI>
        </ul>

        <H2>3. Finalità del trattamento</H2>
        <ul className="list-disc pl-5 space-y-1 mb-3">
          <LI>Gestione dell'account e partecipazione a campionati e tornei.</LI>
          <LI>Comunicazioni di servizio (notifiche partite, email di verifica e promemoria).</LI>
          <LI>Sicurezza, prevenzione abusi e moderazione.</LI>
          <LI>Mostrare annunci pubblicitari tramite Google AdSense (vedi sezione cookie).</LI>
        </ul>

        <H2>4. Cookie e tecnologie simili</H2>
        <P>Il sito utilizza le seguenti categorie di cookie:</P>
        <ul className="list-disc pl-5 space-y-1 mb-3">
          <LI><strong>Cookie tecnici (necessari)</strong>: indispensabili per autenticazione, sessione e preferenze. Non richiedono consenso.</LI>
          <LI><strong>Cookie di terze parti pubblicitari</strong>: il sito usa <strong>Google AdSense</strong> per mostrare annunci. Google e i suoi partner possono usare cookie per personalizzare gli annunci in base alle visite su questo e altri siti. Puoi gestire le preferenze su <a href="https://adssettings.google.com" target="_blank" rel="noreferrer" className="text-[#00F0FF] underline">Impostazioni annunci Google</a> e consultare la <a href="https://policies.google.com/technologies/ads?hl=it" target="_blank" rel="noreferrer" className="text-[#00F0FF] underline">policy pubblicitaria di Google</a>.</LI>
          <LI><strong>Cookie analitici</strong>: utilizziamo strumenti di analisi per capire come viene usato il sito e migliorarlo.</LI>
        </ul>
        <P>
          Al primo accesso puoi scegliere tramite il banner se accettare tutti i cookie o solo quelli
          necessari: rifiutando, gli annunci mostrati saranno <strong>non personalizzati</strong>. Puoi
          modificare la scelta in qualsiasi momento cancellando i dati del sito dal browser.
        </P>

        <H2>5. Base giuridica e conservazione</H2>
        <P>
          Il trattamento si basa sull'esecuzione del servizio richiesto (account e competizioni), sul
          consenso (cookie pubblicitari) e sul legittimo interesse (sicurezza). I dati dell'account sono
          conservati finché l'account è attivo; puoi richiederne la cancellazione in ogni momento.
        </P>

        <H2>6. Condivisione dei dati</H2>
        <P>
          I dati non vengono venduti a terzi. Possono essere condivisi con fornitori tecnici che ci
          aiutano a erogare il servizio (hosting, invio email, notifiche push, Google AdSense per la
          pubblicità) nel rispetto delle normative vigenti. Alcuni fornitori possono trattare dati al di
          fuori dell'UE con garanzie adeguate.
        </P>

        <H2>7. Diritti dell'utente (GDPR)</H2>
        <P>
          Ai sensi del Regolamento UE 2016/679 hai diritto di accedere ai tuoi dati, rettificarli,
          cancellarli, limitarne il trattamento, opporti al trattamento e richiederne la portabilità.
          Puoi esercitare questi diritti contattando lo staff EPT Italy. Hai inoltre diritto di proporre
          reclamo al Garante per la Protezione dei Dati Personali.
        </P>

        <H2>8. Minori</H2>
        <P>
          La piattaforma è destinata a utenti di età pari o superiore a 14 anni. Se ritieni che un minore
          abbia fornito dati senza consenso, contattaci per la rimozione.
        </P>

        <H2>9. Modifiche</H2>
        <P>
          Questa informativa può essere aggiornata; le modifiche rilevanti saranno comunicate sulla
          piattaforma. L'uso continuato del sito dopo le modifiche costituisce accettazione della nuova
          versione.
        </P>
      </div>
    </div>
  );
}
